/**
 * BYO-GCS settings section — Phase 33-16 IAM-grant model
 *
 * Customer flow ("connect" instead of "upload key"):
 *   1. Open your bucket in GCP console → Permissions → Grant Access
 *   2. Paste OUR Blue Monk service account email + role Storage Object Admin
 *   3. Come back here, type project ID + bucket name + region
 *   4. Test Connection → Save
 *
 * No SA JSON is stored anywhere — V4 URL signing uses our default SA,
 * which the customer has granted IAM access on their bucket. Revoke is
 * one click for the customer (remove the IAM grant on their bucket).
 *
 * Design (per apps/frontend/DESIGN.md):
 *   - Single brand-purple accent for primary CTAs
 *   - Emerald = configured / connected; amber = not configured
 *   - 4px grid, shadcn defaults, motion-safe fade-in
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Loader2,
  Send,
  Check,
  Trash2,
  ShieldCheck,
  ShieldAlert,
  Info,
  Copy,
  KeyRound,
  Tag,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc';

const GCS_REGIONS = [
  'us-central1',
  'us-east1',
  'us-east4',
  'us-west1',
  'us-west2',
  'europe-west1',
  'europe-west2',
  'europe-west3',
  'europe-west4',
  'asia-east1',
  'asia-southeast1',
  'asia-southeast2',
  'australia-southeast1',
  'australia-southeast2',
] as const;
type GcsRegion = (typeof GCS_REGIONS)[number];

// GCS bucket naming rules: 3-63 chars, lowercase letters/digits/_.-,
// start+end with letter or digit. https://cloud.google.com/storage/docs/buckets#naming
const GCS_BUCKET_RE = /^[a-z0-9][a-z0-9._-]{1,61}[a-z0-9]$/;

const FOLDER_RE = /^[A-Za-z0-9/_-]+$/;
function isInvalidFolder(v: string): string | null {
  if (v === '') return null;
  if (v.length > 256) return 'too long (max 256)';
  if (v.startsWith('/') || v.endsWith('/')) return 'no leading/trailing slash';
  if (v.includes('..')) return 'no parent traversal (..)';
  if (v.includes('\\') || v.includes('://')) return 'no backslash or absolute URI';
  if (!FOLDER_RE.test(v)) return 'allowed chars: letters, digits, /, -, _';
  // Mirrors the backend `validateFolderPath` — `__CLEAR__` is a sentinel
  // the set-creds RPCs use to delete a Vault entry, so a user typing it
  // literally would silently wipe their config. Reject up-front.
  if (v === '__CLEAR__') return 'reserved value (try a different name)';
  return null;
}

function isGcsRegion(r: string | null | undefined): r is GcsRegion {
  if (typeof r !== 'string') return false;
  return (GCS_REGIONS as readonly string[]).includes(r);
}

export function GcsCloudStorageSection() {
  const settingsQuery = trpc.productionSettings.getGcsSettings.useQuery();
  const updateMutation = trpc.productionSettings.updateGcsSettings.useMutation();
  const clearMutation = trpc.productionSettings.clearGcsSettings.useMutation();
  const testMutation = trpc.productionSettings.testGcs.useMutation();

  const [projectId, setProjectId] = useState('');
  const [bucket, setBucket] = useState('');
  const [region, setRegion] = useState<GcsRegion | ''>('');
  const [customsFolder, setCustomsFolder] = useState('');
  const [printsFolder, setPrintsFolder] = useState('');
  const [bucketRootPrefix, setBucketRootPrefix] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const [labelKeyCopied, setLabelKeyCopied] = useState(false);
  const [labelValueCopied, setLabelValueCopied] = useState(false);

  useEffect(() => {
    if (!settingsQuery.data || loaded) return;
    setProjectId(settingsQuery.data.projectId ?? '');
    setBucket(settingsQuery.data.bucket ?? '');
    if (isGcsRegion(settingsQuery.data.region)) {
      setRegion(settingsQuery.data.region);
    }
    setCustomsFolder(settingsQuery.data.customsFolder ?? '');
    setPrintsFolder(settingsQuery.data.printsFolder ?? '');
    setBucketRootPrefix(settingsQuery.data.bucketRootPrefix ?? '');
    // Auto-expand the optional layout panel if any of those fields are
    // already populated.
    if (
      (settingsQuery.data.customsFolder && settingsQuery.data.customsFolder !== 'custom') ||
      (settingsQuery.data.printsFolder && settingsQuery.data.printsFolder !== 'products') ||
      settingsQuery.data.bucketRootPrefix
    ) {
      setShowAdvanced(true);
    }
    setLoaded(true);
  }, [settingsQuery.data, loaded]);

  const bucketError = bucket === '' ? null : (GCS_BUCKET_RE.test(bucket) ? null : 'Invalid GCS bucket name');
  const customsFolderError = isInvalidFolder(customsFolder);
  const printsFolderError = isInvalidFolder(printsFolder);
  const bucketRootPrefixError = isInvalidFolder(bucketRootPrefix);

  // Required fields for the IAM-grant model: project ID, bucket, region.
  const requiredComplete = projectId.trim().length > 0 && bucket.trim().length > 0 && region !== '';

  const allValid =
    bucketError === null
    && customsFolderError === null
    && printsFolderError === null
    && bucketRootPrefixError === null
    && requiredComplete;

  const hasUnsavedChanges = useMemo(() => {
    if (!loaded) return false;
    if (projectId !== (settingsQuery.data?.projectId ?? '')) return true;
    if (bucket !== (settingsQuery.data?.bucket ?? '')) return true;
    if (region !== (settingsQuery.data?.region ?? '')) return true;
    if (customsFolder !== (settingsQuery.data?.customsFolder ?? '')) return true;
    if (printsFolder !== (settingsQuery.data?.printsFolder ?? '')) return true;
    if (bucketRootPrefix !== (settingsQuery.data?.bucketRootPrefix ?? '')) return true;
    return false;
  }, [loaded, projectId, bucket, region, customsFolder, printsFolder, bucketRootPrefix, settingsQuery.data]);

  const copyTo = (value: string, setFlag: (b: boolean) => void) => {
    if (!value) return;
    navigator.clipboard.writeText(value).then(
      () => {
        setFlag(true);
        window.setTimeout(() => setFlag(false), 2000);
      },
      () => toast.error('Could not copy. Select the text and copy manually.'),
    );
  };

  const handleCopyEmail = () => {
    const email = settingsQuery.data?.serviceAccountEmail;
    if (email) copyTo(email, setEmailCopied);
  };

  const handleSave = () => {
    if (!allValid) return;
    const payload: {
      projectId?: string;
      bucket?: string;
      region?: GcsRegion;
      customsFolder?: string | null;
      printsFolder?: string | null;
      bucketRootPrefix?: string | null;
    } = {};
    if (projectId !== (settingsQuery.data?.projectId ?? '')) payload.projectId = projectId;
    if (bucket !== (settingsQuery.data?.bucket ?? '')) payload.bucket = bucket;
    if (region && region !== (settingsQuery.data?.region ?? '')) payload.region = region;
    if (customsFolder !== (settingsQuery.data?.customsFolder ?? '')) {
      payload.customsFolder = customsFolder === '' ? null : customsFolder;
    }
    if (printsFolder !== (settingsQuery.data?.printsFolder ?? '')) {
      payload.printsFolder = printsFolder === '' ? null : printsFolder;
    }
    if (bucketRootPrefix !== (settingsQuery.data?.bucketRootPrefix ?? '')) {
      payload.bucketRootPrefix = bucketRootPrefix === '' ? null : bucketRootPrefix;
    }
    if (Object.keys(payload).length === 0) return;

    updateMutation.mutate(payload, {
      onSuccess: () => {
        toast.success('Google Cloud Storage settings saved');
        settingsQuery.refetch();
      },
      onError: (err) =>
        toast.error('Failed to save GCS settings', { description: err.message }),
    });
  };

  const handleTest = () => {
    testMutation.mutate(undefined, {
      onSuccess: (result) => {
        if (result.success) {
          toast.success('Bucket reachable', {
            description: `Our service account can read + write ${settingsQuery.data?.bucket ?? 'the bucket'}.`,
          });
        } else {
          const message = 'error' in result ? result.error : 'Unknown error';
          toast.error('Could not reach the bucket', { description: message });
        }
        settingsQuery.refetch();
      },
      onError: (err) => toast.error('Test failed', { description: err.message }),
    });
  };

  const handleClear = () => {
    clearMutation.mutate(undefined, {
      onSuccess: () => {
        toast.success('Bucket disconnected. Reverted to PrintRun default.');
        setProjectId('');
        setBucket('');
        setRegion('');
        setCustomsFolder('');
        setPrintsFolder('');
        setBucketRootPrefix('');
        setShowAdvanced(false);
        settingsQuery.refetch();
      },
      onError: (err) => toast.error('Failed to clear', { description: err.message }),
    });
  };

  const hasCredentials = settingsQuery.data?.hasCredentials ?? false;
  const byoActive = settingsQuery.data?.byoActive ?? false;
  const serviceAccountEmail = settingsQuery.data?.serviceAccountEmail ?? null;

  return (
    <div
      id="gcs-cloud-storage-section"
      className="space-y-4 p-6 rounded-lg border border-border bg-card motion-safe:animate-fade-in"
    >
      <div className="space-y-1">
        <h3 className="text-xl font-semibold">Google Cloud Storage</h3>
        <p className="text-sm text-muted-foreground/90">
          Connect your own GCS bucket. We sign upload URLs with our service account against your
          bucket, so no service-account keys leave your project.
        </p>
      </div>

      {/* Preview-mode banner — shown when BYO_GCS_ACTIVE is false on the
          backend. Admins can configure + Test Connection in this state
          (pre-staging), but the runtime path keeps routing to Blue Monk
          default until the flag flips. */}
      {!byoActive && (
        <div
          role="status"
          className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-3"
        >
          <Info className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" aria-hidden="true" />
          <div className="flex-1 min-w-0 space-y-1">
            <p className="text-sm font-medium text-blue-900">Preview mode</p>
            <p className="text-xs text-blue-800/90">
              BYO-GCS is staged but not yet active at runtime. You can configure + verify your
              bucket here. Print files keep routing to the PrintRun default until ops flips{' '}
              <code>BYO_GCS_ACTIVE</code> on the backend.
            </p>
          </div>
        </div>
      )}

      {/* Status banner — three states:
            - amber: no bucket configured
            - amber: configured but last test failed or never tested
            - emerald: configured + last test succeeded */}
      {(() => {
        if (!hasCredentials) {
          return (
            <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <ShieldAlert className="h-5 w-5 text-amber-600 shrink-0" aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-amber-800">No bucket connected.</p>
                <p className="text-xs text-amber-700/80">
                  Grant Storage Object Admin to our service account on your bucket, then point us
                  at the bucket below.
                </p>
              </div>
            </div>
          );
        }
        const lastTested = settingsQuery.data?.lastTestOutcome;
        const lastTestedAt = settingsQuery.data?.lastTestAt;
        const verified = lastTested === 'success';
        if (verified) {
          return (
            <div
              role="status"
              className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3"
            >
              <ShieldCheck className="h-5 w-5 text-emerald-600 shrink-0" aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-emerald-800">
                  {byoActive ? 'Bucket connected.' : 'Bucket configured (preview mode).'}
                </p>
                <p className="text-xs text-emerald-700/80">
                  {settingsQuery.data?.bucket} · {settingsQuery.data?.region}
                  {lastTestedAt && (
                    <> · last verified {new Date(lastTestedAt).toLocaleString()}</>
                  )}
                </p>
              </div>
            </div>
          );
        }
        // Configured but not yet verified (or last test failed)
        return (
          <div
            role="status"
            className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3"
          >
            <ShieldAlert className="h-5 w-5 text-amber-600 shrink-0" aria-hidden="true" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-amber-800">
                {lastTested === 'credentials_rejected'
                  ? 'We can’t reach the bucket.'
                  : 'Bucket saved, not yet verified.'}
              </p>
              <p className="text-xs text-amber-700/80">
                {settingsQuery.data?.bucket} · {settingsQuery.data?.region}
                {lastTested === 'credentials_rejected'
                  ? '. Confirm the IAM grant is in place, then click Test connection.'
                  : '. Run Test connection to confirm the IAM grant is in place.'}
              </p>
            </div>
          </div>
        );
      })()}

      {settingsQuery.isPending && (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      )}

      {settingsQuery.data && (
        <div className="space-y-5 motion-safe:animate-fade-in">
          {/* Step 1 — service account email + instructions */}
          <section aria-labelledby="gcs-step-grant" className="space-y-3">
            <div className="flex items-baseline gap-2">
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-threadheads-purple/10 text-xs font-semibold text-threadheads-purple">
                1
              </span>
              <h4 id="gcs-step-grant" className="text-lg font-medium tracking-tight">
                Grant our service account access in Google Cloud
              </h4>
            </div>
            <div className="ml-8 space-y-3">
              <ol className="list-decimal list-outside space-y-1 pl-5 text-sm text-muted-foreground/90 marker:text-muted-foreground/60">
                <li>
                  In Google Cloud Console, open your bucket →{' '}
                  <span className="font-medium text-foreground">Permissions</span> →{' '}
                  <span className="font-medium text-foreground">Grant access</span>.
                </li>
                <li>
                  Paste the principal below, then add{' '}
                  <span className="font-medium text-foreground">both</span> of these roles and save:
                  <ul className="mt-1 list-disc list-outside space-y-0.5 pl-5 marker:text-muted-foreground/60">
                    <li>
                      <span className="font-medium text-foreground">Storage Object Admin</span>{' '}
                      <span className="text-xs text-muted-foreground/70">
                        (read + write print files in the bucket)
                      </span>
                    </li>
                    <li>
                      <span className="font-medium text-foreground">Storage Legacy Bucket Reader</span>{' '}
                      <span className="text-xs text-muted-foreground/70">
                        (read the bucket label so we can verify ownership; the "Legacy" name is GCP's,
                        the role isn't deprecated)
                      </span>
                    </li>
                  </ul>
                </li>
                <li>Come back here and fill in the fields in step 2.</li>
              </ol>
              <div className="rounded-md border border-border bg-muted/40 p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-2">
                    <KeyRound className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
                    {serviceAccountEmail ? (
                      <code className="truncate text-sm font-mono text-foreground" title={serviceAccountEmail}>
                        {serviceAccountEmail}
                      </code>
                    ) : (
                      <span className="text-sm text-muted-foreground italic">
                        Service account principal unavailable. Check backend env (
                        <code>GCS_DEFAULT_SERVICE_ACCOUNT_JSON</code>).
                      </span>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyEmail}
                    disabled={!serviceAccountEmail || emailCopied}
                    className="h-8 gap-1.5 text-xs"
                    aria-label="Copy service account principal"
                  >
                    {emailCopied ? (
                      <>
                        <Check className="h-3.5 w-3.5" aria-hidden="true" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {/* Step 2 — bucket ownership label */}
          <section aria-labelledby="gcs-step-label" className="space-y-3">
            <div className="flex items-baseline gap-2">
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-threadheads-purple/10 text-xs font-semibold text-threadheads-purple">
                2
              </span>
              <h4 id="gcs-step-label" className="text-lg font-medium tracking-tight">
                Tag your bucket so we know it's yours
              </h4>
            </div>
            <div className="ml-8 space-y-3">
              <ol className="list-decimal list-outside space-y-1 pl-5 text-sm text-muted-foreground/90 marker:text-muted-foreground/60">
                <li>
                  In Google Cloud Console, open your bucket →{' '}
                  <span className="font-medium text-foreground">Labels</span> →{' '}
                  <span className="font-medium text-foreground">+ Add label</span>.
                </li>
                <li>Paste the key and value below, then save.</li>
              </ol>
              <p className="text-xs text-muted-foreground/80">
                This label is the ownership proof we check at save time. Without it, anyone could
                accidentally point their config at someone else's bucket.
              </p>
              <div className="grid gap-2 sm:grid-cols-[auto_1fr_auto] sm:items-center">
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground/70">
                  Key
                </span>
                <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 p-2.5">
                  <Tag className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
                  <code className="truncate text-sm font-mono text-foreground">
                    threadheads-org-id
                  </code>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyTo('threadheads-org-id', setLabelKeyCopied)}
                  disabled={labelKeyCopied}
                  className="h-8 gap-1.5 text-xs"
                  aria-label="Copy label key"
                >
                  {labelKeyCopied ? (
                    <>
                      <Check className="h-3.5 w-3.5" aria-hidden="true" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                      Copy
                    </>
                  )}
                </Button>
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground/70 sm:row-start-2">
                  Value
                </span>
                <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 p-2.5 sm:row-start-2">
                  <Tag className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
                  {settingsQuery.data?.organizationId ? (
                    <code className="truncate text-sm font-mono text-foreground" title={settingsQuery.data.organizationId}>
                      {settingsQuery.data.organizationId}
                    </code>
                  ) : (
                    <span className="text-sm text-muted-foreground italic">unavailable</span>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    settingsQuery.data?.organizationId &&
                    copyTo(settingsQuery.data.organizationId, setLabelValueCopied)
                  }
                  disabled={!settingsQuery.data?.organizationId || labelValueCopied}
                  className="h-8 gap-1.5 text-xs sm:row-start-2"
                  aria-label="Copy label value"
                >
                  {labelValueCopied ? (
                    <>
                      <Check className="h-3.5 w-3.5" aria-hidden="true" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
            </div>
          </section>

          {/* Step 3 — bucket pointer */}
          <section aria-labelledby="gcs-step-pointer" className="space-y-3">
            <div className="flex items-baseline gap-2">
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-threadheads-purple/10 text-xs font-semibold text-threadheads-purple">
                3
              </span>
              <h4 id="gcs-step-pointer" className="text-lg font-medium tracking-tight">
                Tell us where your bucket lives
              </h4>
            </div>
            <div className="ml-8 grid gap-4 sm:grid-cols-2">
              {/* Project ID */}
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="gcs-project-id" className="text-sm font-medium">
                  Project ID
                </Label>
                <Input
                  id="gcs-project-id"
                  type="text"
                  placeholder="threadheads-prod"
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                  className="h-10 font-mono"
                  autoComplete="off"
                />
              </div>

              {/* Bucket */}
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="gcs-bucket" className="text-sm font-medium">
                  Bucket name
                </Label>
                <Input
                  id="gcs-bucket"
                  type="text"
                  placeholder="threadheads-prints-au-syd"
                  value={bucket}
                  onChange={(e) => setBucket(e.target.value)}
                  className={`h-10 font-mono transition-colors duration-200 motion-reduce:transition-none ${bucketError ? 'border-destructive' : ''}`}
                  aria-invalid={bucketError !== null}
                  aria-describedby="gcs-bucket-helper"
                  autoComplete="off"
                />
                <p
                  id="gcs-bucket-helper"
                  className={`text-xs min-h-[1rem] ${bucketError ? 'text-destructive' : 'text-muted-foreground/80'}`}
                >
                  {bucketError ?? 'Lowercase letters, digits, dots, hyphens, underscores. 3 to 63 chars.'}
                </p>
              </div>

              {/* Region */}
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="gcs-region" className="text-sm font-medium">
                  Region
                </Label>
                <Select
                  value={region}
                  onValueChange={(v) => {
                    if (isGcsRegion(v)) setRegion(v);
                  }}
                >
                  <SelectTrigger id="gcs-region" className="h-10">
                    <SelectValue placeholder="Select region" />
                  </SelectTrigger>
                  <SelectContent>
                    {GCS_REGIONS.map((r) => (
                      <SelectItem key={r} value={r}>
                        {r}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground/80">
                  Must match where you created the bucket.
                </p>
              </div>
            </div>
          </section>

          {/* Optional — folder layout */}
          <section aria-labelledby="gcs-step-layout" className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-baseline gap-2">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
                  4
                </span>
                <h4 id="gcs-step-layout" className="text-lg font-medium tracking-tight">
                  Folder layout <span className="text-muted-foreground/70 font-normal">(optional)</span>
                </h4>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowAdvanced((v) => !v)}
                className="h-7 text-xs text-muted-foreground hover:text-foreground"
                aria-expanded={showAdvanced}
                aria-controls="gcs-layout-fields"
              >
                {showAdvanced ? 'Hide' : 'Customise'}
              </Button>
            </div>
            {showAdvanced && (
              <div
                id="gcs-layout-fields"
                className="ml-8 grid gap-4 sm:grid-cols-2 motion-safe:animate-fade-in"
              >
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="gcs-bucket-root-prefix" className="text-sm font-medium">
                    Bucket root prefix
                  </Label>
                  <Input
                    id="gcs-bucket-root-prefix"
                    type="text"
                    placeholder="(empty = bucket root)"
                    value={bucketRootPrefix}
                    onChange={(e) => setBucketRootPrefix(e.target.value)}
                    className={`h-10 font-mono transition-colors duration-200 motion-reduce:transition-none ${bucketRootPrefixError ? 'border-destructive' : ''}`}
                    aria-invalid={bucketRootPrefixError !== null}
                    aria-describedby="gcs-bucket-root-prefix-helper"
                    autoComplete="off"
                  />
                  <p
                    id="gcs-bucket-root-prefix-helper"
                    className={`text-xs min-h-[1rem] ${bucketRootPrefixError ? 'text-destructive' : 'text-muted-foreground/80'}`}
                  >
                    {bucketRootPrefixError ?? (
                      <>
                        Where everything sits inside your bucket. Default empty = bucket root.
                        Nested paths allowed (e.g. <code>arx4</code> or <code>print-files/au</code>).
                      </>
                    )}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gcs-customs-folder" className="text-sm font-medium">
                    Customs folder
                  </Label>
                  <Input
                    id="gcs-customs-folder"
                    type="text"
                    placeholder="custom"
                    value={customsFolder}
                    onChange={(e) => setCustomsFolder(e.target.value)}
                    className={`h-10 font-mono transition-colors duration-200 motion-reduce:transition-none ${customsFolderError ? 'border-destructive' : ''}`}
                    aria-invalid={customsFolderError !== null}
                    aria-describedby="gcs-customs-folder-helper"
                    autoComplete="off"
                  />
                  <p
                    id="gcs-customs-folder-helper"
                    className={`text-xs min-h-[1rem] ${customsFolderError ? 'text-destructive' : 'text-muted-foreground/80'}`}
                  >
                    {customsFolderError ?? <>CUST-* artwork. Default <code>custom</code>.</>}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gcs-prints-folder" className="text-sm font-medium">
                    Prints folder
                  </Label>
                  <Input
                    id="gcs-prints-folder"
                    type="text"
                    placeholder="products"
                    value={printsFolder}
                    onChange={(e) => setPrintsFolder(e.target.value)}
                    className={`h-10 font-mono transition-colors duration-200 motion-reduce:transition-none ${printsFolderError ? 'border-destructive' : ''}`}
                    aria-invalid={printsFolderError !== null}
                    aria-describedby="gcs-prints-folder-helper"
                    autoComplete="off"
                  />
                  <p
                    id="gcs-prints-folder-helper"
                    className={`text-xs min-h-[1rem] ${printsFolderError ? 'text-destructive' : 'text-muted-foreground/80'}`}
                  >
                    {printsFolderError ?? <>Standard product files. Default <code>products</code>.</>}
                  </p>
                </div>
              </div>
            )}
          </section>

          {/* Actions */}
          <div className="flex flex-col gap-2 pt-2 border-t border-border/60 sm:flex-row sm:items-center sm:justify-between">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  disabled={!hasCredentials || clearMutation.isPending}
                  className="h-9 gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/5"
                >
                  {clearMutation.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  ) : (
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  )}
                  Disconnect bucket
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Disconnect this bucket?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Your project ID, bucket name, region, and folder settings are removed from our
                    Vault. Locations on GCS revert to the PrintRun default bucket. Files already
                    in your bucket are not touched. The IAM grant on your side also remains;
                    revoke it in Google Cloud Console if you want to lock us out fully.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleClear}>Disconnect bucket</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                variant="outline"
                onClick={handleTest}
                disabled={!hasCredentials || testMutation.isPending}
                className="h-9 gap-1.5 sm:min-w-[140px]"
              >
                {testMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                ) : (
                  <Send className="h-3.5 w-3.5" aria-hidden="true" />
                )}
                Test connection
              </Button>
              <Button
                onClick={handleSave}
                disabled={!hasUnsavedChanges || !allValid || updateMutation.isPending}
                className="h-9 gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90 sm:min-w-[140px]"
              >
                {updateMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                ) : (
                  <Check className="h-3.5 w-3.5" aria-hidden="true" />
                )}
                Save settings
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
